import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/context/auth-context";
import { BookOpen } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { useCoursesData } from "@/lib/hooks/useCoursesData";
import { CourseStats } from "@/components/courses/CourseStats";
import { CourseFilters } from "@/components/courses/CourseFilters";
import { CourseCard } from "@/components/courses/CourseCard";
import { CoursePagination } from "@/components/courses/CoursePagination";
import { EmptyState } from "@/components/courses/EmptyState";
import { DashboardHeader } from "../components/DashboardHeader";

const Courses = () => {
  const { profile } = useAuth();
  const navigate = useNavigate();
  
  // Use our custom hook to manage all course-related data and actions
  const { 
    courses,
    filteredCourses,
    favoriteIds,
    stats,
    currentPage,
    totalPages,
    indexOfFirstCourse,
    indexOfLastCourse,
    searchQuery,
    eventType,
    sortOrder,
    isLoadingCourses,
    setCurrentPage,
    setSearchQuery,
    setEventType,
    setSortOrder,
    saveToFavorites,
    handleEnroll,
    handleCancelRegistration,
    getRegistrationStatus,
    isVerified,
    getStateRequirementsList
  } = useCoursesData(profile?.id);

  // Handle toggling favorite with event stop propagation
  const handleToggleFavorite = (courseId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    saveToFavorites(courseId);
  };

  return (
    <div className="w-full px-4 sm:px-6 py-4">
      <DashboardHeader
        title="Available Courses"
        subtitle="Browse and enroll in professional development opportunities"
      />

      {/* Stats Cards */}
      <CourseStats 
        totalCourses={stats.totalCourses}
        liveCourses={stats.liveCourses}
        selfPacedCourses={stats.selfPacedCourses}
        enrolledCourses={stats.enrolledCourses}
      />

      {/* Filters */}
      <CourseFilters 
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        eventType={eventType}
        setEventType={setEventType}
        sortOrder={sortOrder}
        setSortOrder={setSortOrder}
        totalResults={filteredCourses.length}
      />

      <Separator className="bg-accent-light/30 my-4" />

      {/* Course Listings */}
      {isLoadingCourses ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="animate-pulse border rounded-lg h-80">
              <div className="h-10 bg-accent-light/20 rounded-t-lg"></div>
              <div className="p-4 space-y-3">
                <div className="h-6 bg-accent-light/30 rounded w-3/4"></div>
                <div className="h-4 bg-accent-light/20 rounded w-full"></div>
                <div className="h-4 bg-accent-light/20 rounded w-2/3"></div>
                <div className="pt-4 grid grid-cols-2 gap-2">
                  <div className="h-4 bg-accent-light/20 rounded"></div>
                  <div className="h-4 bg-accent-light/20 rounded"></div>
                </div>
              </div>
              <div className="mt-auto p-4 border-t">
                <div className="h-8 bg-accent-light/30 rounded w-full"></div>
              </div>
            </div>
          ))}
        </div>
      ) : filteredCourses.length > 0 ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {courses.map((course) => (
              <CourseCard
                      key={course.id} 
                course={course}
                isFavorite={favoriteIds.includes(course.id)}
                registrationStatus={getRegistrationStatus(course.id)}
                isVerified={isVerified(course.id)}
                stateRequirements={getStateRequirementsList(course)}
                onEnroll={handleEnroll}
                onCancelRegistration={handleCancelRegistration}
                onToggleFavorite={handleToggleFavorite}
              />
                                          ))}
                                        </div>

          <CoursePagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            totalResults={filteredCourses.length}
            startItem={indexOfFirstCourse + 1}
            endItem={Math.min(indexOfLastCourse, filteredCourses.length)}
          />
        </>
      ) : (
        <EmptyState 
          searchQuery={searchQuery} 
          onClearSearch={() => setSearchQuery('')} 
        />
      )}
    </div>
  );
};

export default Courses;
